import { useGetFertilizers } from '@practium/features/api';
import { useToast, type FormSelectOption } from '@practium/ui';
import { useMemo, useState } from 'react';
import { usePostAdviceCorrectionFosfatagem } from '../../api/hooks/recommendations';
import { FosfatagemSchema } from '../schemas/correction.schema';
import { useCorrectionStore } from '../store/correctionStore';
import type { CorrectionStartResponse, FosfatagemFormData, FosfatagemResult } from '../types';
import { useTabCalculation } from './useTabCalculation';

const START_KEYS: Record<string, string[]> = {
	'P-M1': ['Pm1'],
	'K+': ['K'],
	'Sat. K': ['satK'],
};

const startValue = (
	start: CorrectionStartResponse,
	column: 'atual' | 'faixaAdequada',
	param: string
): string => {
	for (const key of START_KEYS[param] ?? [param]) {
		const value = start[column]?.[key];
		if (value !== null && value !== undefined && value !== '') return String(value);
	}
	return '—';
};

const applyStartToTable = (
	result: FosfatagemResult,
	start: CorrectionStartResponse
): FosfatagemResult => ({
	...result,
	table: result.table.map((row) => ({
		...row,
		current: startValue(start, 'atual', row.param),
		range: startValue(start, 'faixaAdequada', row.param),
	})),
});

const retainTableContext = (
	next: FosfatagemResult,
	previous: FosfatagemResult | null
): FosfatagemResult => ({
	...next,
	table: next.table.map((row) => {
		const old = previous?.table.find((candidate) => candidate.param === row.param);
		return old ? { ...row, current: old.current, range: old.range } : row;
	}),
});

export const useFosfatagemTab = () => {
	const toast = useToast();
	const {
		advice,
		correctionStart,
		fosfatagem,
		fosfatagemAjuste,
		fosfatagemResult,
		fosfatagemHasCalculated,
		setFosfatagemField,
		setFosfatagemAjusteField,
		setFosfatagemResult,
		setFosfatagemHasCalculated,
		resetFosfatagem,
	} = useCorrectionStore();
	const { data: fertilizers = [], isLoading: isLoadingFertilizers } = useGetFertilizers();
	const fertilizerOptions = useMemo<FormSelectOption[]>(
		() =>
			fertilizers
				.filter(
					(product) =>
						(product.category === 'fertilizer' || product.category === 'organic') &&
						product.nature === 'solid'
				)
				.map((product) => ({
					value: product._id,
					label: `${product.name} (N: ${product.N ?? 0}, P₂O₅: ${product.P2O5 ?? 0}, K₂O: ${product.K2O ?? 0})`,
				})),
		[fertilizers]
	);

	const { mutateAsync: calcularAdubacao } = usePostAdviceCorrectionFosfatagem();
	const [isAdjusting, setIsAdjusting] = useState(false);

	const selectedProducts = () =>
		[
			fosfatagem.fertilizanteP
				? { productId: fosfatagem.fertilizanteP, dose: Number(fosfatagemAjuste.dose || 0) }
				: null,
			fosfatagem.fertilizanteComplementar
				? {
						productId: fosfatagem.fertilizanteComplementar,
						dose: Number(fosfatagemAjuste.doseComplementar || 0),
					}
				: null,
		].filter((product): product is { productId: string; dose: number } => product !== null);

	const updateAdjustment = (result: FosfatagemResult) => {
		setFosfatagemAjusteField('dose', result.ajuste.dose);
		setFosfatagemAjusteField('doseSecundaria', result.ajuste.doseSecundaria);
		setFosfatagemAjusteField('doseComplementar', result.ajuste.doseComplementar ?? '');
		setFosfatagemAjusteField(
			'doseSecundariaComplementar',
			result.ajuste.doseSecundariaComplementar ?? ''
		);
	};

	const calculateResult = async (form: FosfatagemFormData) => {
		const adviceId = advice?._id;
		if (!adviceId) throw new Error('Recomendação não encontrada para calcular fosfatagem.');
		const result = await calcularAdubacao({
			adviceId,
			products: selectedProducts(),
			forma: form.forma as 'sulco' | 'cova',
			formaConfig: {
				p: Number(form.profundidade),
				l: Number(form.largura),
				...(form.forma === 'cova' ? { c: Number(form.comprimento) } : {}),
			},
		});
		updateAdjustment(result);
		return correctionStart ? applyStartToTable(result, correctionStart) : result;
	};

	const tabCalculation = useTabCalculation<FosfatagemFormData, FosfatagemResult>({
		form: fosfatagem,
		ajuste: fosfatagemAjuste,
		result: fosfatagemResult,
		hasCalculated: fosfatagemHasCalculated,
		setField: setFosfatagemField,
		setAjusteField: setFosfatagemAjusteField,
		setResult: setFosfatagemResult,
		setHasCalculated: setFosfatagemHasCalculated,
		reset: resetFosfatagem,
		schema: FosfatagemSchema,
		calculate: calculateResult,
		tabName: 'Fosfatagem',
		onSetField: (key, value) => {
			if (key === 'fertilizanteP' && value === fosfatagem.fertilizanteComplementar) {
				setFosfatagemField('fertilizanteP', '');
				toast.info('Esse produto já está selecionado.');
			}
			if (key === 'fertilizanteComplementar' && value === fosfatagem.fertilizanteP) {
				setFosfatagemField('fertilizanteComplementar', '');
				toast.info('Esse produto já está selecionado.');
			}
			setFosfatagemResult(null);
			setFosfatagemHasCalculated(false);
		},
	});

	const setComplementaryDose = (value: string) =>
		setFosfatagemAjusteField('doseComplementar', value);

	const handleAdjustDoses = async () => {
		const products = selectedProducts();
		if (products.length === 0 || products.some(({ dose }) => !Number.isFinite(dose) || dose < 0)) {
			toast.error('Informe doses válidas para recalcular a fosfatagem.');
			return;
		}
		setIsAdjusting(true);
		try {
			const result = await calculateResult(fosfatagem);
			const mappedResult = retainTableContext(result, fosfatagemResult);
			setFosfatagemResult(mappedResult);
			updateAdjustment(mappedResult);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao recalcular fosfatagem.');
		} finally {
			setIsAdjusting(false);
		}
	};

	return {
		...tabCalculation,
		ajuste: fosfatagemAjuste,
		fertilizerOptions,
		isLoadingFertilizers,
		setComplementaryDose,
		handleAdjustDoses,
		isAdjusting,
	};
};
